import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../Config';

const AsientoManualForm = ({ onBack }) => {
    const [glosa, setGlosa] = useState('');
    const [fechaAsiento, setFechaAsiento] = useState(new Date().toISOString().split('T')[0]);
    const [detalles, setDetalles] = useState([
        { cuentaContableId: '', debe: '', haber: '' },
        { cuentaContableId: '', debe: '', haber: '' }
    ]);
    const [cuentas, setCuentas] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchCuentas = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API_BASE_URL}/plan-cuentas`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Filter only imputable accounts
                setCuentas(res.data.filter(c => c.esImputable));
            } catch (err) {
                console.error('Error fetching cuentas', err);
            }
        };
        fetchCuentas();
    }, []);

    const handleDetalleChange = (index, field, value) => {
        const newDetalles = [...detalles];
        newDetalles[index][field] = value;
        
        // Si carga debe, limpia haber y viceversa
        if (field === 'debe' && value !== '') newDetalles[index]['haber'] = '';
        if (field === 'haber' && value !== '') newDetalles[index]['debe'] = '';
        
        setDetalles(newDetalles);
    };

    const addDetalle = () => {
        setDetalles([...detalles, { cuentaContableId: '', debe: '', haber: '' }]);
    };

    const removeDetalle = (index) => {
        if (detalles.length <= 2) return;
        const newDetalles = detalles.filter((_, i) => i !== index);
        setDetalles(newDetalles);
    };

    const calcularTotales = () => {
        let totalDebe = 0;
        let totalHaber = 0;
        detalles.forEach(d => {
            totalDebe += Number(d.debe) || 0;
            totalHaber += Number(d.haber) || 0;
        });
        return { totalDebe, totalHaber };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        const { totalDebe, totalHaber } = calcularTotales();
        
        if (totalDebe === 0 && totalHaber === 0) {
            setError('El asiento debe tener montos.');
            return;
        }
        
        if (totalDebe !== totalHaber) {
            setError(`El asiento no cuadra. Diferencia: ${Math.abs(totalDebe - totalHaber)}`);
            return;
        }

        // Validate accounts
        const invalidRow = detalles.find(d => !d.cuentaContableId || (d.debe === '' && d.haber === ''));
        if (invalidRow) {
            setError('Todas las filas deben tener cuenta asignada y un monto.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const payload = {
                glosa,
                fechaAsiento,
                detalles: detalles.map(d => ({
                    cuentaContableId: parseInt(d.cuentaContableId),
                    debe: Number(d.debe) || 0,
                    haber: Number(d.haber) || 0
                }))
            };
            
            await axios.post(`${API_BASE_URL}/contabilidad/asiento`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            alert('Asiento registrado exitosamente');
            onBack();
        } catch (err) {
            setError(err.response?.data?.error || 'Error al registrar el asiento');
        }
    };

    const { totalDebe, totalHaber } = calcularTotales();

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h2>Registrar Asiento Manual</h2>
                <button className="btn btn-secondary" onClick={onBack}>Volver al Diario</button>
            </div>
            <div className="card-body">
                {error && <div className="alert alert-danger">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                        <div className="col-md-9">
                            <label>Glosa o Concepto</label>
                            <input type="text" className="form-control" value={glosa} onChange={e => setGlosa(e.target.value)} required />
                        </div>
                        <div className="col-md-3">
                            <label>Fecha</label>
                            <input type="date" className="form-control" value={fechaAsiento} onChange={e => setFechaAsiento(e.target.value)} required />
                        </div>
                    </div>

                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th>Cuenta Contable</th>
                                <th>Debe</th>
                                <th>Haber</th>
                                <th>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detalles.map((d, idx) => (
                                <tr key={idx}>
                                    <td>
                                        <select 
                                            className="form-control select2" 
                                            value={d.cuentaContableId} 
                                            onChange={e => handleDetalleChange(idx, 'cuentaContableId', e.target.value)}
                                            required
                                        >
                                            <option value="">Seleccione cuenta...</option>
                                            {cuentas.map(c => (
                                                <option key={c.id} value={c.id}>{c.codigoIncoop} - {c.nombreCuenta}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            value={d.debe} 
                                            onChange={e => handleDetalleChange(idx, 'debe', e.target.value)} 
                                            disabled={d.haber !== '' && d.haber > 0}
                                        />
                                    </td>
                                    <td>
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            value={d.haber} 
                                            onChange={e => handleDetalleChange(idx, 'haber', e.target.value)} 
                                            disabled={d.debe !== '' && d.debe > 0}
                                        />
                                    </td>
                                    <td className="text-center">
                                        {detalles.length > 2 && (
                                            <button type="button" className="btn btn-sm btn-danger" onClick={() => removeDetalle(idx)}>
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="4">
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addDetalle}>
                                        <i className="fas fa-plus"></i> Agregar Fila
                                    </button>
                                </td>
                            </tr>
                            <tr className={totalDebe !== totalHaber ? 'table-danger' : 'table-success'}>
                                <td className="text-right"><b>Totales:</b></td>
                                <td className="text-right"><b>{new Intl.NumberFormat('es-PY').format(totalDebe)}</b></td>
                                <td className="text-right"><b>{new Intl.NumberFormat('es-PY').format(totalHaber)}</b></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                    
                    <div className="d-flex justify-content-end mt-3">
                        <button type="submit" className="btn btn-success" disabled={totalDebe === 0 || totalDebe !== totalHaber}>
                            <i className="fas fa-save"></i> Guardar Asiento
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AsientoManualForm;
